import { useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import toast from "react-hot-toast";
import { Event, EventUsers } from "../../models/events";
import {
  CancellationReason,
  CANCELLATION_INTERNAL_NOTE_MAX_LENGTH,
  isOtherReason,
} from "../../models/eventCancellation";
import {
  useDeactivateEventMutation,
  useGetEventUsersQuery,
} from "../../store/services/events";
import {
  useCancelEventMutation,
  useGetCancellationReasonsQuery,
} from "../../store/services/event-cancellations";
import { useIsFeatureFlagEnabled } from "../../hooks/useIsFeatureFlagEnabled";
import { getApiErrorMessage } from "../../utils/functions/api-errors";

const REAL_USER_TYPE_ID = 2;

type CancelEventDialogProps = {
  visible: boolean;
  event: Event | undefined;
  onHide: () => void;
};

const countRealUsers = (eventUsers: EventUsers[] | undefined): number =>
  (eventUsers ?? []).filter((user) => user.userTypeId === REAL_USER_TYPE_ID).length;

const countPaidRealUsers = (eventUsers: EventUsers[] | undefined): number =>
  (eventUsers ?? []).filter(
    (user) => user.userTypeId === REAL_USER_TYPE_ID && Boolean(user.paid)
  ).length;

export const CancelEventDialog = ({ visible, event, onHide }: CancelEventDialogProps) => {
  const [reasonId, setReasonId] = useState<number | null>(null);
  const [internalNote, setInternalNote] = useState("");
  const [showErrors, setShowErrors] = useState(false);

  const eventId = event?.id;

  useEffect(() => {
    if (visible) {
      return;
    }
    setReasonId(null);
    setInternalNote("");
    setShowErrors(false);
  }, [visible]);

  const { enabled: requiresCancellationReason, loading: isLoadingFlag } = useIsFeatureFlagEnabled(
    "admin_event_cancellation",
    event?.tenantId
  );

  const { data: eventUsers } = useGetEventUsersQuery(eventId ?? 0, {
    skip: !visible || !eventId,
  });
  const { data: reasons, isLoading: isLoadingReasons } = useGetCancellationReasonsQuery(
    { tenantId: event?.tenantId ?? null },
    { skip: !visible || !requiresCancellationReason }
  );

  const [cancelEvent, { isLoading: isCancelling }] = useCancelEventMutation();
  const [deactivateEvent, { isLoading: isDeactivating }] = useDeactivateEventMutation();
  const isSubmitting = isCancelling || isDeactivating;
  const isConfirmDisabled =
    isSubmitting || isLoadingFlag || (requiresCancellationReason && isLoadingReasons);

  const activeReasons = useMemo(
    () => (reasons ?? []).filter((reason) => reason.active),
    [reasons]
  );
  const selectedReason = activeReasons.find((reason) => reason.id === reasonId);
  const requiresNote = isOtherReason(selectedReason?.reasonText);
  const trimmedNote = internalNote.trim();

  const inscribedCount = eventUsers ? countRealUsers(eventUsers) : event?.users ?? 0;
  const paidCount = countPaidRealUsers(eventUsers);

  const handleConfirm = async () => {
    if (!eventId || isConfirmDisabled) return;

    if (requiresCancellationReason && (!reasonId || (requiresNote && !trimmedNote))) {
      setShowErrors(true);
      return;
    }

    try {
      if (requiresCancellationReason && reasonId) {
        await cancelEvent({
          eventId,
          reasonId,
          internalNote: trimmedNote || null,
        }).unwrap();
      } else {
        await deactivateEvent(eventId).unwrap();
      }
      onHide();
    } catch (err: unknown) {
      const reason = getApiErrorMessage(err) ?? "el servidor rechazó el cambio de estado";
      toast.error(`No se pudo cambiar el estado del evento: ${reason}`);
    }
  };

  return (
    <Dialog
      header={
        <div className="flex align-items-center gap-2">
          <i className="pi pi-exclamation-triangle text-red-500"></i>
          <span>Desactivar evento</span>
        </div>
      }
      visible={visible}
      modal
      style={{ width: "34rem", maxWidth: "95vw", maxHeight: "90vh" }}
      breakpoints={{ "960px": "80vw", "641px": "95vw" }}
      contentStyle={{ overflowY: "auto" }}
      appendTo={document.body}
      onHide={onHide}
      footer={
        <div className="flex flex-column-reverse sm:flex-row justify-content-end gap-2">
          <Button
            severity="secondary"
            outlined
            label="Cancelar"
            icon="pi pi-times"
            disabled={isSubmitting}
            onClick={onHide}
          />
          <Button
            severity="danger"
            label="Desactivar"
            icon="pi pi-eye-slash"
            disabled={isConfirmDisabled}
            loading={isConfirmDisabled}
            onClick={handleConfirm}
          />
        </div>
      }
    >
      <div className="flex flex-column gap-4">
        {!!event?.name && (
          <div>
            <small className="text-600 block">Evento</small>
            <span className="font-medium text-900 line-height-3">
              {event.name}
              <span className="text-500 font-normal"> (ID #{event.id})</span>
            </span>
          </div>
        )}

        <Message
          className="w-full"
          severity="warn"
          content={
            <div className="flex align-items-start gap-2 line-height-3">
              <i className="pi pi-exclamation-triangle mt-1"></i>
              <span>
                Esta acción es <strong>irreversible</strong>: una vez desactivado, el evento
                no puede volver a activarse. En la App pasa a <strong>Cancelado</strong> y,
                en caso de corresponder, se procesan los reembolsos de los inscriptos.
              </span>
            </div>
          }
        />

        {requiresCancellationReason && (
          <div className="flex flex-column gap-3">
            <div>
              <label htmlFor="cancellation-reason" className="font-medium text-900">
                Motivo de cancelación *
              </label>
              <Dropdown
                inputId="cancellation-reason"
                className={`w-full mt-2 ${showErrors && !reasonId ? "p-invalid" : ""}`}
                value={reasonId}
                options={activeReasons}
                optionLabel="reasonText"
                optionValue="id"
                placeholder={
                  isLoadingReasons ? "Cargando motivos…" : "Seleccioná un motivo"
                }
                valueTemplate={(reason: CancellationReason | null | undefined) =>
                  reason?.reasonText ??
                  (isLoadingReasons ? "Cargando motivos…" : "Seleccioná un motivo")
                }
                itemTemplate={(reason: CancellationReason) => (
                  <span className="white-space-normal">{reason.reasonText}</span>
                )}
                disabled={isSubmitting || isLoadingReasons}
                appendTo={document.body}
                scrollHeight="220px"
                panelStyle={{ maxWidth: "min(32rem, 95vw)", zIndex: 2000 }}
                onChange={(e) => setReasonId(e.value ?? null)}
              />
              {showErrors && !reasonId && (
                <small className="p-error block mt-1">
                  El motivo de cancelación es obligatorio.
                </small>
              )}
            </div>

            <div>
              <label htmlFor="cancellation-internal-note" className="font-medium text-900">
                Observación interna {requiresNote ? "*" : "(opcional)"}
              </label>
              <InputTextarea
                id="cancellation-internal-note"
                className={`w-full mt-2 ${
                  showErrors && requiresNote && !trimmedNote ? "p-invalid" : ""
                }`}
                value={internalNote}
                maxLength={CANCELLATION_INTERNAL_NOTE_MAX_LENGTH}
                rows={3}
                autoResize={false}
                disabled={isSubmitting}
                onChange={(e) => setInternalNote(e.target.value)}
              />
              <div className="flex justify-content-between align-items-start gap-2 mt-1">
                {showErrors && requiresNote && !trimmedNote ? (
                  <small className="p-error">
                    La observación interna es obligatoria cuando el motivo es "Otro".
                  </small>
                ) : (
                  <span />
                )}
                <small className="text-500 white-space-nowrap">
                  {internalNote.length}/{CANCELLATION_INTERNAL_NOTE_MAX_LENGTH}
                </small>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <div
            className="flex-1 surface-ground border-1 surface-border border-round p-3"
            style={{ minWidth: "9rem" }}
          >
            <small className="text-600 block">Inscriptos</small>
            <span className="text-2xl font-bold text-900">{inscribedCount}</span>
          </div>
          <div
            className="flex-1 surface-ground border-1 surface-border border-round p-3"
            style={{ minWidth: "9rem" }}
          >
            <small className="text-600 block">Pagos registrados</small>
            <span
              className={`text-2xl font-bold ${paidCount > 0 ? "text-orange-600" : "text-900"}`}
            >
              {paidCount}
            </span>
          </div>
        </div>

        {paidCount > 0 && (
          <Message
            className="w-full justify-content-start"
            severity="warn"
            text={`Se procesarán reembolsos por ${paidCount} ${
              paidCount === 1 ? "pago registrado" : "pagos registrados"
            }.`}
          />
        )}
      </div>
    </Dialog>
  );
};
